import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import { useCatalog } from '../app/providers/CatalogProvider.jsx';
import { fetchAdConfig } from '../services/ads/adConfigService.js';
import { loadPayPalSdk } from '../services/commerce/commerceService.js';
import {
  captureAdvertisingOrder,
  createAdvertisingOrder,
  submitAdvertisingCampaign,
} from '../services/business/businessService.js';
import '../styles/business.css';

const VIDEO_OFFERS = {
  currency: 'USD',
  cpm: { '15': '20.00', '30': '30.00' },
  flatPackages: [
    { key: 'starter', label: 'Starter', impressions: 5000, prices: { '15': '100.00', '30': '150.00' } },
    { key: 'standard', label: 'Standard', impressions: 10000, prices: { '15': '200.00', '30': '300.00' } },
    { key: 'growth', label: 'Growth', impressions: 25000, prices: { '15': '475.00', '30': '700.00' } },
    { key: 'reach', label: 'Reach', impressions: 50000, prices: { '15': '900.00', '30': '1300.00' } },
  ],
  daily: { minBudget: { '15': '20.00', '30': '30.00' }, maxBudget: '5000.00' },
};

const DISPLAY_OFFERS = [
  { key: 'starter', label: 'Starter', impressions: 10000, price: '25.00', cpm: '2.50' },
  { key: 'standard', label: 'Standard', impressions: 25000, price: '60.00', cpm: '2.40' },
  { key: 'growth', label: 'Growth', impressions: 50000, price: '110.00', cpm: '2.20' },
  { key: 'reach', label: 'Reach', impressions: 100000, price: '200.00', cpm: '2.00' },
];

function formatInt(value) {
  return Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function money(value) {
  const number = Number(value || 0);
  return number.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(number) ? 0 : 2,
  });
}

function AdvertisingNav() {
  return (
    <nav className="business-subnav" aria-label="WatchVIM advertising">
      <NavLink to="/advertise">Overview</NavLink>
      <NavLink to="/advertise/video">Video Ads</NavLink>
      <NavLink to="/advertise/display">Display Ads</NavLink>
      <NavLink to="/advertise/pricing">Pricing</NavLink>
      <NavLink to="/advertise/dashboard">Campaign Report</NavLink>
      <NavLink to="/advertise/sponsorships">Sponsorships</NavLink>
    </nav>
  );
}

function CheckoutShell({ title, subtitle, children }) {
  return (
    <section className="business-page advertising-checkout-page">
      <header className="business-hero advertising-checkout-hero">
        <span className="eyebrow">WatchVIM for Brands</span>
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </header>
      <div className="business-content">
        <AdvertisingNav />
        <div className="ad-checkout-progress" aria-label="Campaign checkout steps">
          <span><b>1</b> Campaign</span>
          <span><b>2</b> Creative</span>
          <span><b>3</b> Review & Pay</span>
        </div>
        {children}
      </div>
    </section>
  );
}

function StatusMessage({ error, success, status }) {
  if (error) return <p className="business-message error" role="alert">{error}</p>;
  if (success) return <p className="business-message success" role="status">{success}</p>;
  if (status) return <p className="business-message" role="status">{status}</p>;
  return null;
}

function Section({ step, title, description, children }) {
  return (
    <section className="ad-checkout-section">
      <div className="ad-checkout-section-heading">
        <span>{step}</span>
        <div><h2>{title}</h2>{description ? <p>{description}</p> : null}</div>
      </div>
      <div className="ad-checkout-section-body">{children}</div>
    </section>
  );
}

function SecurePayBox({ paypalHostRef, error, success, status, reportUrl }) {
  return (
    <section className="ad-checkout-paybox">
      <div className="ad-secure-row">
        <span className="ad-secure-icon" aria-hidden="true">✓</span>
        <div><strong>Secure checkout</strong><p>Payment is processed by PayPal. WatchVIM verifies the completed order on the server before your campaign enters approval.</p></div>
      </div>
      <StatusMessage error={error} success={success} status={status} />
      {reportUrl ? <NavLink className="business-primary" to={reportUrl}>Open Campaign Report</NavLink> : null}
      <div ref={paypalHostRef} className="paypal-button-host ad-paypal-host" aria-label="PayPal advertising checkout" />
      <p className="business-fineprint">Your campaign begins counting impressions only after WatchVIM approves the creative. Purchased campaigns run until their impression goal is delivered across eligible inventory.</p>
    </section>
  );
}

function BrandFields() {
  return (
    <>
      <div className="business-field-row two">
        <label>Company name<input name="companyName" autoComplete="organization" required /></label>
        <label>Contact name<input name="contactName" autoComplete="name" required /></label>
      </div>
      <div className="business-field-row two">
        <label>Email<input name="email" type="email" autoComplete="email" required /></label>
        <label>Company website<input name="companyUrl" type="url" placeholder="https://" required /></label>
      </div>
    </>
  );
}

function TargetingFields({ publishedTitles, placementScope, setPlacementScope, targetTitleId, setTargetTitleId }) {
  return (
    <>
      <label>Placement scope
        <select name="placementScope" value={placementScope} onChange={(event) => setPlacementScope(event.target.value)}>
          <option value="platform">Platform-wide eligible inventory</option>
          <option value="title">Target a published WatchVIM title</option>
        </select>
      </label>
      {placementScope === 'title' ? (
        <label>Target title
          <select value={targetTitleId} onChange={(event) => setTargetTitleId(event.target.value)} required>
            <option value="">Select a title…</option>
            {publishedTitles.map((title) => <option key={title.id} value={title.id}>{title.title}</option>)}
          </select>
        </label>
      ) : null}
    </>
  );
}

function inspectBrowserVideo(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    const finish = () => URL.revokeObjectURL(url);
    video.onloadedmetadata = () => {
      const result = {
        width: Number(video.videoWidth || 0),
        height: Number(video.videoHeight || 0),
        duration: Number(video.duration || 0),
      };
      finish();
      resolve(result);
    };
    video.onerror = () => {
      finish();
      reject(new Error('The MP4 could not be inspected in your browser. Re-export it as H.264/AAC MP4 and try again.'));
    };
    video.src = url;
  });
}

export function VideoAdvertisingCheckoutPage() {
  const { catalog } = useCatalog();
  const [params] = useSearchParams();
  const formRef = useRef(null);
  const paypalHostRef = useRef(null);
  const buttonsRef = useRef(null);
  const paidOrderRef = useRef('');
  const [config, setConfig] = useState(null);
  const [campaignType, setCampaignType] = useState(params.get('duration') === '30' ? '30' : '15');
  const [billingMode, setBillingMode] = useState(params.get('mode') === 'daily' ? 'daily' : 'flat');
  const [packageKey, setPackageKey] = useState(params.get('package') || 'starter');
  const [dailyBudget, setDailyBudget] = useState('20');
  const [flightDays, setFlightDays] = useState('7');
  const [placementScope, setPlacementScope] = useState('platform');
  const [targetTitleId, setTargetTitleId] = useState('');
  const [status, setStatus] = useState('Loading secure checkout…');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [reportUrl, setReportUrl] = useState('');

  useEffect(() => { fetchAdConfig().then(setConfig).catch(() => {}); }, []);
  const offers = config?.advertiserOffers || VIDEO_OFFERS;
  const publishedTitles = useMemo(() => (catalog?.titles || []).slice().sort((a, b) => a.title.localeCompare(b.title)), [catalog?.titles]);
  const targetTitle = publishedTitles.find((title) => title.id === targetTitleId) || null;
  const selectedPackage = (offers.flatPackages || VIDEO_OFFERS.flatPackages).find((pkg) => pkg.key === packageKey) || VIDEO_OFFERS.flatPackages[0];
  const cpm = Number(offers.cpm?.[campaignType] || VIDEO_OFFERS.cpm[campaignType]);
  const minDailyBudget = Number(offers.daily?.minBudget?.[campaignType] || VIDEO_OFFERS.daily.minBudget[campaignType]);
  const safeDailyBudget = Math.max(minDailyBudget, Number(dailyBudget || 0));
  const safeDays = Math.min(30, Math.max(1, Number(flightDays || 1)));
  const dailyImpressions = Math.floor((safeDailyBudget / cpm) * 1000);
  const quotedAmount = billingMode === 'daily' ? safeDailyBudget * safeDays : Number(selectedPackage?.prices?.[campaignType] || 0);
  const quotedImpressions = billingMode === 'daily' ? dailyImpressions * safeDays : Number(selectedPackage?.impressions || 0);

  async function validateForm() {
    const form = formRef.current;
    if (!form) return 'Campaign form is unavailable.';
    const data = new FormData(form);
    for (const key of ['companyName', 'contactName', 'email', 'companyUrl']) {
      if (!String(data.get(key) || '').trim()) return 'Please complete all required company and contact fields.';
    }
    if (placementScope === 'title' && !targetTitleId) return 'Select the WatchVIM title this campaign should target.';
    if (billingMode === 'daily' && Number(dailyBudget) < minDailyBudget) return `Daily budget must be at least ${money(minDailyBudget)}.`;
    if (billingMode === 'daily' && (Number(flightDays) < 1 || Number(flightDays) > 30)) return 'Daily campaigns must run between 1 and 30 days.';
    const media = data.get('media');
    const logo = data.get('logo');
    if (!(media instanceof File) || !media.name) return 'Upload your video ad creative before checkout.';
    if (String(media.type || '').toLowerCase() !== 'video/mp4' || !/\.mp4$/i.test(media.name)) return 'Video creative must be an MP4 file.';
    if (media.size > 200 * 1024 * 1024) return 'Video creative must be 200 MB or smaller.';
    if (!(logo instanceof File) || !logo.name) return 'Upload your company logo before checkout.';

    try {
      const metadata = await inspectBrowserVideo(media);
      if (metadata.width !== 1920 || metadata.height !== 1080) {
        return `Video resolution must be exactly 1920 × 1080. This file is ${metadata.width || '?'} × ${metadata.height || '?'}.`;
      }
      const expectedDuration = Number(campaignType);
      if (!Number.isFinite(metadata.duration) || Math.abs(metadata.duration - expectedDuration) > 0.15) {
        return `Video duration must match the purchased ${campaignType}-second spot. This file is ${Number.isFinite(metadata.duration) ? metadata.duration.toFixed(2) : 'unknown'} seconds.`;
      }
    } catch (error) {
      return error?.message || 'The video creative could not be inspected.';
    }
    return '';
  }

  useEffect(() => {
    if (!paypalHostRef.current) return undefined;
    let active = true;
    loadPayPalSdk({ intent: 'capture' }).then((paypal) => {
      if (!active || !paypalHostRef.current) return;
      try { buttonsRef.current?.close?.(); } catch (_) {}
      paypalHostRef.current.innerHTML = '';
      const buttons = paypal.Buttons({
        style: { shape: 'rect', color: 'gold', layout: 'vertical', label: 'pay', height: 48 },
        createOrder: async () => {
          const problem = await validateForm();
          if (problem) { setError(problem); throw new Error(problem); }
          setError(''); setSuccess(''); setReportUrl(''); setStatus('Creating secure WatchVIM order…');
          const order = await createAdvertisingOrder({
            campaignFormat: 'video', campaignType, billingMode, packageKey,
            dailyBudget: billingMode === 'daily' ? dailyBudget : undefined,
            flightDays: billingMode === 'daily' ? flightDays : undefined,
          });
          if (!order?.id) throw new Error('PayPal did not return an advertising order.');
          paidOrderRef.current = String(order.id);
          setStatus(`Ready to pay ${money(order.watchvim_amount)} for ${formatInt(order.watchvim_impression_goal)} impressions.`);
          return order.id;
        },
        onApprove: async (data) => {
          const orderId = String(data?.orderID || paidOrderRef.current || '').trim();
          if (!orderId) throw new Error('PayPal returned without an order reference.');
          setStatus('Verifying payment and creating your campaign…');
          const captured = await captureAdvertisingOrder(orderId);
          if (String(captured?.status || '').toUpperCase() !== 'COMPLETED') throw new Error('PayPal has not completed this campaign payment.');
          const formData = new FormData(formRef.current);
          formData.set('campaignFormat', 'video');
          formData.set('campaignType', campaignType);
          formData.set('billingMode', billingMode);
          formData.set('packageKey', packageKey);
          formData.set('dailyBudget', billingMode === 'daily' ? dailyBudget : '');
          formData.set('flightDays', billingMode === 'daily' ? flightDays : '');
          formData.set('placementScope', placementScope);
          formData.set('targetTitleId', placementScope === 'title' ? targetTitleId : '');
          formData.set('targetTitleName', placementScope === 'title' ? (targetTitle?.title || '') : '');
          formData.set('orderId', orderId);
          try {
            const result = await submitAdvertisingCampaign(formData);
            setStatus('');
            setSuccess(`Payment confirmed. Campaign ${result?.item?.id || orderId} is now awaiting WatchVIM creative approval.`);
            setReportUrl(result?.reportUrl || '');
            paidOrderRef.current = '';
          } catch (submitError) {
            setStatus('');
            setError(`Payment was captured, but campaign setup did not finish. Do not pay again. Keep PayPal order ${orderId} and email advertise@watchvim.com. ${submitError?.message || ''}`.trim());
          }
        },
        onCancel: () => { setStatus('Checkout cancelled. No campaign was submitted.'); setError(''); },
        onError: (err) => { setStatus(''); setError(err?.message || 'PayPal checkout could not be completed.'); },
      });
      buttonsRef.current = buttons;
      setStatus('');
      return buttons.render(paypalHostRef.current);
    }).catch((err) => { if (active) { setStatus(''); setError(err?.message || 'PayPal checkout is unavailable.'); } });
    return () => { active = false; try { buttonsRef.current?.close?.(); } catch (_) {} };
  }, [campaignType, billingMode, packageKey, dailyBudget, flightDays, placementScope, targetTitleId, targetTitle?.title, minDailyBudget]);

  return (
    <CheckoutShell title="Build Your Video Ad Campaign" subtitle="Choose your reach, upload your 15- or 30-second creative, review the order, and pay securely with PayPal.">
      <div className="business-two-column ad-checkout-grid">
        <form ref={formRef} className="business-form ad-checkout-form" onSubmit={(event) => event.preventDefault()}>
          <Section step="01" title="Advertiser details" description="Tell us who is running the campaign."><BrandFields /></Section>
          <Section step="02" title="Campaign setup" description="Choose your video length, delivery model, and targeting.">
            <div className="business-field-row two">
              <label>Video length<select name="campaignType" value={campaignType} onChange={(event) => setCampaignType(event.target.value)}><option value="15">15 seconds · ${offers.cpm?.['15'] || '20'} CPM</option><option value="30">30 seconds · ${offers.cpm?.['30'] || '30'} CPM</option></select></label>
              <label>Purchase type<select name="billingMode" value={billingMode} onChange={(event) => setBillingMode(event.target.value)}><option value="flat">Guaranteed impression package</option><option value="daily">Daily budget</option></select></label>
            </div>
            {billingMode === 'flat' ? (
              <label>Impression package<select name="packageKey" value={packageKey} onChange={(event) => setPackageKey(event.target.value)}>{(offers.flatPackages || VIDEO_OFFERS.flatPackages).map((pkg) => <option key={pkg.key} value={pkg.key}>{pkg.label} · {formatInt(pkg.impressions)} impressions · {money(pkg.prices?.[campaignType])}</option>)}</select></label>
            ) : (
              <div className="business-field-row two">
                <label>Daily budget<input name="dailyBudget" type="number" min={minDailyBudget} max="5000" step="1" value={dailyBudget} onChange={(event) => setDailyBudget(event.target.value)} /></label>
                <label>Campaign days<input name="flightDays" type="number" min="1" max="30" step="1" value={flightDays} onChange={(event) => setFlightDays(event.target.value)} /></label>
              </div>
            )}
            <div className="business-field-row two">
              <label>Ad position<select name="adPosition" defaultValue="preroll"><option value="preroll">Pre-roll</option><option value="midroll">Mid-roll</option><option value="both">Pre-roll + Mid-roll</option></select></label>
              <TargetingFields publishedTitles={publishedTitles} placementScope={placementScope} setPlacementScope={setPlacementScope} targetTitleId={targetTitleId} setTargetTitleId={setTargetTitleId} />
            </div>
          </Section>
          <Section step="03" title="Upload creative" description={`Upload the final ${campaignType}-second video and your company logo.`}>
            <label>{campaignType}-second video creative<input name="media" type="file" accept="video/mp4,.mp4" required /></label>
            <label>Company logo<input name="logo" type="file" accept="image/png,image/jpeg,image/webp" required /></label>
            <label>Optional third-party VAST URL<input name="vastUrl" type="url" placeholder="https://" /></label>
            <p className="business-fineprint"><strong>WatchVIM video specs:</strong> MP4 · H.264/AVC · 1920 × 1080 progressive · 16:9 · AAC stereo at 48 kHz · 23.98, 24, 29.97, or 30 fps · exactly {campaignType} seconds · recommended 8–12 Mbps · maximum 200 MB.</p>
            <p className="business-fineprint">Your file is checked in the browser before checkout and fully validated by WatchVIM after payment before the campaign is accepted. If a paid creative fails validation, replace the file using the same order — do not pay again.</p>
          </Section>
          <SecurePayBox paypalHostRef={paypalHostRef} error={error} success={success} status={status} reportUrl={reportUrl} />
        </form>

        <aside className="business-card sticky-card ad-order-summary">
          <span className="business-kicker">Order Summary</span>
          <div className="ad-summary-product"><span>Video advertising</span><strong>{campaignType} seconds</strong></div>
          <strong className="business-price">{money(quotedAmount)}</strong>
          <div className="ad-summary-row"><span>Purchased impressions</span><strong>{formatInt(quotedImpressions)}</strong></div>
          <div className="ad-summary-row"><span>Effective CPM</span><strong>{money(cpm)}</strong></div>
          <div className="ad-summary-row"><span>Purchase</span><strong>{billingMode === 'daily' ? `${safeDays} day flight` : selectedPackage?.label}</strong></div>
          {billingMode === 'daily' ? <div className="ad-summary-row"><span>Daily delivery cap</span><strong>{formatInt(dailyImpressions)}</strong></div> : null}
          <div className="ad-summary-divider" />
          <p className="ad-summary-note">✓ Server-verified PayPal payment<br />✓ Creative approval before delivery<br />✓ Impression goal automatically enforced<br />✓ Private campaign reporting</p>
        </aside>
      </div>
    </CheckoutShell>
  );
}

export function DisplayAdvertisingCheckoutPage() {
  const { catalog } = useCatalog();
  const [params] = useSearchParams();
  const formRef = useRef(null);
  const paypalHostRef = useRef(null);
  const buttonsRef = useRef(null);
  const paidOrderRef = useRef('');
  const [packageKey, setPackageKey] = useState(params.get('package') || 'starter');
  const [placementScope, setPlacementScope] = useState('platform');
  const [targetTitleId, setTargetTitleId] = useState('');
  const [status, setStatus] = useState('Loading secure checkout…');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [reportUrl, setReportUrl] = useState('');
  const publishedTitles = useMemo(() => (catalog?.titles || []).slice().sort((a, b) => a.title.localeCompare(b.title)), [catalog?.titles]);
  const targetTitle = publishedTitles.find((title) => title.id === targetTitleId) || null;
  const selectedPackage = DISPLAY_OFFERS.find((pkg) => pkg.key === packageKey) || DISPLAY_OFFERS[0];

  function validateForm() {
    const form = formRef.current;
    if (!form) return 'Campaign form is unavailable.';
    const data = new FormData(form);
    for (const key of ['companyName', 'contactName', 'email', 'companyUrl']) {
      if (!String(data.get(key) || '').trim()) return 'Please complete all required company and contact fields.';
    }
    if (placementScope === 'title' && !targetTitleId) return 'Select the WatchVIM title this campaign should target.';
    const media = data.get('media');
    const logo = data.get('logo');
    if (!(media instanceof File) || !media.name) return 'Upload your display image before checkout.';
    if (!(logo instanceof File) || !logo.name) return 'Upload your company logo before checkout.';
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(String(media.type || '').toLowerCase())) return 'Display creative must be PNG, JPEG, or WEBP.';
    return '';
  }

  useEffect(() => {
    if (!paypalHostRef.current) return undefined;
    let active = true;
    loadPayPalSdk({ intent: 'capture' }).then((paypal) => {
      if (!active || !paypalHostRef.current) return;
      try { buttonsRef.current?.close?.(); } catch (_) {}
      paypalHostRef.current.innerHTML = '';
      const buttons = paypal.Buttons({
        style: { shape: 'rect', color: 'gold', layout: 'vertical', label: 'pay', height: 48 },
        createOrder: async () => {
          const problem = validateForm();
          if (problem) { setError(problem); throw new Error(problem); }
          setError(''); setSuccess(''); setReportUrl(''); setStatus('Creating secure WatchVIM display order…');
          const order = await createAdvertisingOrder({ campaignFormat: 'display', billingMode: 'flat', packageKey });
          if (!order?.id) throw new Error('PayPal did not return an advertising order.');
          paidOrderRef.current = String(order.id);
          setStatus(`Ready to pay ${money(order.watchvim_amount)} for ${formatInt(order.watchvim_impression_goal)} display impressions.`);
          return order.id;
        },
        onApprove: async (data) => {
          const orderId = String(data?.orderID || paidOrderRef.current || '').trim();
          if (!orderId) throw new Error('PayPal returned without an order reference.');
          setStatus('Verifying payment and creating your image campaign…');
          const captured = await captureAdvertisingOrder(orderId);
          if (String(captured?.status || '').toUpperCase() !== 'COMPLETED') throw new Error('PayPal has not completed this campaign payment.');
          const formData = new FormData(formRef.current);
          formData.set('campaignFormat', 'display');
          formData.set('billingMode', 'flat');
          formData.set('packageKey', packageKey);
          formData.set('adPosition', 'display');
          formData.set('placementScope', placementScope);
          formData.set('targetTitleId', placementScope === 'title' ? targetTitleId : '');
          formData.set('targetTitleName', placementScope === 'title' ? (targetTitle?.title || '') : '');
          formData.set('orderId', orderId);
          try {
            const result = await submitAdvertisingCampaign(formData);
            setStatus('');
            setSuccess(`Payment confirmed. Display campaign ${result?.item?.id || orderId} is awaiting WatchVIM creative approval.`);
            setReportUrl(result?.reportUrl || '');
            paidOrderRef.current = '';
          } catch (submitError) {
            setStatus('');
            setError(`Payment was captured, but campaign setup did not finish. Do not pay again. Keep PayPal order ${orderId} and email advertise@watchvim.com. ${submitError?.message || ''}`.trim());
          }
        },
        onCancel: () => { setStatus('Checkout cancelled. No campaign was submitted.'); setError(''); },
        onError: (err) => { setStatus(''); setError(err?.message || 'PayPal checkout could not be completed.'); },
      });
      buttonsRef.current = buttons;
      setStatus('');
      return buttons.render(paypalHostRef.current);
    }).catch((err) => { if (active) { setStatus(''); setError(err?.message || 'PayPal checkout is unavailable.'); } });
    return () => { active = false; try { buttonsRef.current?.close?.(); } catch (_) {} };
  }, [packageKey, placementScope, targetTitleId, targetTitle?.title]);

  return (
    <CheckoutShell title="Launch a Display Image Campaign" subtitle="Start at $25 for 10,000 guaranteed image impressions across eligible WatchVIM display inventory.">
      <div className="ad-display-package-strip" aria-label="Display advertising packages">
        {DISPLAY_OFFERS.map((pkg) => (
          <button type="button" className={pkg.key === packageKey ? 'active' : ''} key={pkg.key} onClick={() => setPackageKey(pkg.key)}>
            <span>{pkg.label}</span><strong>{money(pkg.price)}</strong><small>{formatInt(pkg.impressions)} impressions</small>
          </button>
        ))}
      </div>
      <div className="business-two-column ad-checkout-grid">
        <form ref={formRef} className="business-form ad-checkout-form" onSubmit={(event) => event.preventDefault()}>
          <Section step="01" title="Advertiser details" description="Tell us who is running the campaign."><BrandFields /></Section>
          <Section step="02" title="Campaign setup" description="Choose your guaranteed impression package and targeting.">
            <label>Display package<select name="packageKey" value={packageKey} onChange={(event) => setPackageKey(event.target.value)}>{DISPLAY_OFFERS.map((pkg) => <option key={pkg.key} value={pkg.key}>{pkg.label} · {formatInt(pkg.impressions)} impressions · {money(pkg.price)}</option>)}</select></label>
            <TargetingFields publishedTitles={publishedTitles} placementScope={placementScope} setPlacementScope={setPlacementScope} targetTitleId={targetTitleId} setTargetTitleId={setTargetTitleId} />
          </Section>
          <Section step="03" title="Upload image creative" description="Upload your final campaign image and company logo. Recommended companion size: 300 × 250 pixels.">
            <label>Display image<input name="media" type="file" accept="image/png,image/jpeg,image/webp" required /></label>
            <label>Company logo<input name="logo" type="file" accept="image/png,image/jpeg,image/webp" required /></label>
            <p className="business-fineprint">PNG, JPEG, or WEBP. Keep important copy and logos inside a safe margin. The image may scale responsively inside eligible WatchVIM placements.</p>
          </Section>
          <SecurePayBox paypalHostRef={paypalHostRef} error={error} success={success} status={status} reportUrl={reportUrl} />
        </form>

        <aside className="business-card sticky-card ad-order-summary">
          <span className="business-kicker">Order Summary</span>
          <div className="ad-summary-product"><span>Display image advertising</span><strong>{selectedPackage.label}</strong></div>
          <strong className="business-price">{money(selectedPackage.price)}</strong>
          <div className="ad-summary-row"><span>Guaranteed impressions</span><strong>{formatInt(selectedPackage.impressions)}</strong></div>
          <div className="ad-summary-row"><span>Effective CPM</span><strong>{money(selectedPackage.cpm)}</strong></div>
          <div className="ad-summary-row"><span>Placement</span><strong>{placementScope === 'title' ? 'Title-targeted' : 'Platform-wide'}</strong></div>
          <div className="ad-summary-divider" />
          <p className="ad-summary-note">✓ Starts at $25<br />✓ Impression goal automatically enforced<br />✓ Click + CTR reporting<br />✓ Private campaign dashboard</p>
        </aside>
      </div>
    </CheckoutShell>
  );
}
